"use client"
import { ComponentConfig } from "@measured/puck"
import { Modal } from "antd"
import { useEffect, useRef, useState } from "react"
import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"
import { configs } from "../../../../external-components"
import useIsMobile from "../../../lib/use-is-mobile"
import "./media.css"

export interface MediaItems {
  item: MediaProps[]
}

export interface MediaProps {
  country?: string
  city?: string
  coverImage?: string
  previewImage?: string[]
}

// const locations: MediaItems[] = [
//   {
//     id: 1,
//     country: "Thailand",
//     city: "Bangkok",
//     coverImage: "ssss",
//     previewImage: ["ss", "", Images.imageThaiLan],
//     slug: "bangkok",
//   },

// ]

export const RenderCustomMedia: ComponentConfig<MediaItems> = {
  render: ({ item }) => {
    const [selectedMedia, setSelectedMedia] = useState<MediaProps[] | null>(null)
    const [currentImageIndex, setCurrentImageIndex] = useState(0)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [currentIndex, setCurrentIndex] = useState(0)
    const [isDragging, setIsDragging] = useState(false)
    const [startX, setStartX] = useState(0)

    const scrollContainerRef = useRef<HTMLDivElement | null>(null)

    const slidesToShow = 4
    const totalSlides = item.length

    const handlePrev = () => {
      setCurrentIndex(prevIndex => (prevIndex - slidesToShow + totalSlides) % totalSlides)
    }

    const handleNext = () => {
      setCurrentIndex(prevIndex => (prevIndex + slidesToShow) % totalSlides)
    }
    const handleItemClick = (location: any) => {
      setSelectedMedia(location)
      setCurrentImageIndex(0)
      setIsModalOpen(true)
    }
    const handleThumbnailClick = (index: number) => {
      setCurrentImageIndex(index)
    }

    const handleModalClose = () => {
      setIsModalOpen(false)
      setSelectedMedia(null)
      setCurrentImageIndex(0)
    }

    const getAllImages = (location: any) => {
      return location
    }

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      setIsDragging(true)
      setStartX(e.pageX - (scrollContainerRef.current?.offsetLeft || 0))
      if (scrollContainerRef.current) {
        scrollContainerRef.current.style.scrollBehavior = "auto" // Disable smooth scroll while dragging
      }
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      if (scrollContainerRef.current) {
        scrollContainerRef.current.style.scrollBehavior = "smooth" // Re-enable smooth scroll after dragging
      }
    }

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isDragging || !scrollContainerRef.current) return
      const x = e.pageX - scrollContainerRef.current.offsetLeft
      const walk = (x - startX) * 1.5 // Adjust scroll speed
      scrollContainerRef.current.scrollLeft -= walk
      setStartX(x)
    }

    const handleMouseLeave = () => {
      if (isDragging) {
        setIsDragging(false) // Stop dragging when the cursor leaves the container
        if (scrollContainerRef.current) {
          scrollContainerRef.current.style.scrollBehavior = "smooth" // Re-enable smooth scroll on leave
        }
      }
    }

    useEffect(() => {
      if (!scrollContainerRef.current) return

      const container = scrollContainerRef.current
      const thumbnails = container.querySelectorAll("button")
      const activeThumbnail = thumbnails[currentImageIndex]

      if (activeThumbnail) {
        const containerWidth = container.clientWidth
        const thumbnailWidth = activeThumbnail.clientWidth
        const thumbnailOffsetLeft = activeThumbnail.offsetLeft

        const scrollPosition = thumbnailOffsetLeft - containerWidth / 2 + thumbnailWidth / 2

        container.scrollTo({
          left: scrollPosition,
          behavior: "smooth",
        })
      }
    }, [currentImageIndex])

    const isMobile = useIsMobile()

    return (
      <>
        <div className="py-[65px] px-4">
          <h2 className="text-[64px] leading-[72px] font-bold text-center">Media</h2>
          <div className="w-full mt-[67px]">
            <div className="relative overflow-hidden">
              <div
                className="flex flex-col gap-10 sm:gap-0 sm:flex-row transition-transform duration-500 w-full sm:h-[540px] group/slider"
                style={
                  !isMobile
                    ? {
                        transform: `translateX(-${(currentIndex / item.length) * 100}%)`,
                        width: `${(item.length / slidesToShow) * 100}%`,
                      }
                    : {}
                }
              >
                {item.map((slide, index) => (
                  <div
                    onClick={() => handleItemClick(slide.previewImage)}
                    key={index}
                    className="sm:mx-2.5 sm:w-1/4 relative transition-all duration-300 sm:group/slide sm:hover:w-1/3 flex flex-col gap-6 sm:group-hover/slider:opacity-50 hover:!opacity-100 hover:cursor-pointer"
                  >
                    <div className="overflow-hidden rounded-lg shadow-lg w-full h-[462px] sm:group-hover/slide:border-b-[6px] sm:group-hover/slide:border-[#0B9444] transition-all duration-300 ease-in-out">
                      <img
                        src={`${configs.API_URL}${slide.coverImage}`}
                        alt={`${slide.city}, ${slide.country}`}
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                    <div className="text-center">
                      <h3 className="font-bold text-[#242F3E] text-2xl leading-7">{slide.country}</h3>
                      <div className="flex flex-row items-center justify-center gap-3">
                        <div className="h-[1px] w-[68px] bg-gray-300 rounded-full shadow-md opacity-50"></div>
                        <p className="text-sm text-gray-500">{slide.city}</p>
                        <div className="h-[1px] w-[68px] bg-gray-300 rounded-full shadow-md opacity-50"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center mt-[67px]">
              <div className="justify-center gap-2 lg:flex hidden">
                <button
                  className="flex p-1 rounded-full w-10 h-10 items-center justify-center"
                  style={{ backgroundColor: "#EBEDF0" }}
                  onClick={() => handlePrev()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#000000"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-chevron-left"
                  >
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                </button>
                <button
                  className="flex p-1 rounded-full w-10 h-10 items-center justify-center"
                  style={{ backgroundColor: "#EBEDF0" }}
                  onClick={() => handleNext()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#000000"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-chevron-right"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <Modal
            open={isModalOpen}
            onCancel={handleModalClose}
            footer={null}
            width={"100vw"}
            centered
            closable={false}
            className="fullscreen-modal z-20"
            styles={{
              content: {
                backgroundColor: "rgba(0, 0, 0, 0.9)",
                padding: 0,
                margin: 0,
                zIndex: 20,
              },
              mask: {
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                zIndex: 20,
              },
              body: {
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 20,
              },
            }}
          >
            {selectedMedia && (
              <div className="relative h-screen w-screen bg-black overflow-hidden z-20">
                <button
                  onClick={handleModalClose}
                  className="absolute top-[10%] sm:top-[195px] left-4 sm:left-[120px] p-2 bg-[#FFFFFF80] text-white rounded-full z-20 backdrop-blur-[4px] w-10 h-10"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#FFFFFF"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-chevron-left w-full h-full"
                  >
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                </button>

                <div className="relative h-full w-full flex items-center justify-center">
                  <img
                    src={`${configs.API_URL}${selectedMedia[currentImageIndex].previewImage}`}
                    alt={`${selectedMedia[currentImageIndex].country}`}
                    className="h-screen w-screen object-cover transition-all duration-500 transform"
                  />
                  <div className="absolute top-[15%] sm:top-[283px] right-6 sm:right-[120px] text-white text-right flex flex-col gap-1 rounded-lg">
                    <h2 className="text-[64px] leading-[72px] font-bold drop-shadow-lg sm:text-shadow-md">
                      {selectedMedia[currentImageIndex].country}
                    </h2>
                    <div className="gap-4 flex flex-row items-center justify-center w-full">
                      <div className="h-px w-full bg-white"></div>
                      <p className="text-2xl leading-[24px] drop-shadow-lg sm:text-shadow-md">
                        {selectedMedia[currentImageIndex].city}
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  className="absolute bottom-0 left-0 right-0 sm:px-[120px]"
                  onMouseDown={handleMouseDown}
                  onMouseUp={handleMouseUp}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  style={{
                    background: "linear-gradient(to bottom, #00000000 0%, #000000 100%)",
                  }}
                >
                  <div className="flex flex-row gap-1 justify-center">
                    {selectedMedia.map((image: any, index: number) => (
                      <div
                        className={`w-10 h-1 rounded-full bg-white transform transition-all duration-500 cursor-pointer ${
                          currentImageIndex === index ? "opacity-100" : "opacity-50"
                        }`}
                        key={index}
                        onClick={() => handleThumbnailClick(index)}
                      />
                    ))}
                  </div>
                  <div
                    className="flex pl-6 sm:justify-center gap-6 sm:px-[120px] py-12 overflow-x-auto custom-scrollbar-hidden"
                    ref={scrollContainerRef}
                    style={{ display: "flex", whiteSpace: "nowrap", cursor: isDragging ? "grabbing" : "grab" }}
                  >
                    {selectedMedia.map((image: any, index: number) => (
                      <button
                        key={index}
                        onClick={() => handleThumbnailClick(index)}
                        className={`relative h-[131px] w-[192px] overflow-hidden rounded-lg transition-all duration-300 ${
                          currentImageIndex === index ? "border border-white rounded-2xl" : "border border-transparent"
                        }`}
                        style={{ flexShrink: 0 }}
                      >
                        <img
                          src={`${configs.API_URL}${image.previewImage}`}
                          alt={`Preview ${index + 1}`}
                          className="h-full w-full object-cover"
                        />
                        {currentImageIndex !== index && <div className="absolute inset-0 bg-black/30" />}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </Modal>
        </div>
      </>
    )
  },
}
